import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ScrollTools from "@/components/feature/ScrollTools";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import CaseCard from "@/components/feature/CaseCard";
import StatusBadge from "@/components/base/StatusBadge";
import { useSeo } from "@/hooks/useSeo";
import { cases, caseCategories, caseStatuses } from "@/mocks/cases";

const sortOptions = [
  { value: "heat", label: "按热度排序" },
  { value: "updated", label: "按最近更新" },
  { value: "title", label: "按标题排列" },
];

export default function Cases() {
  const [params, setParams] = useSearchParams();
  const initialCategory = params.get("category") ?? "全部";
  const [keyword, setKeyword] = useState(params.get("q") ?? "");
  const [category, setCategory] = useState(
    caseCategories.includes(initialCategory) ? initialCategory : "全部",
  );
  const [status, setStatus] = useState("全部");
  const [sort, setSort] = useState("heat");
  const [view, setView] = useState<"grid" | "list">("grid");

  useSeo({
    title: "案例库｜反转剧情与双方回应完整档案 - 瓜田黑料",
    description:
      "瓜田黑料的完整案例档案库，按文娱现场、网络热点、消费维权、公益舆情分类收录，每条案例包含正序时间线、双方回应、舆论风向变化与澄清汇总，支持关键词检索与多维筛选。",
    keywords: "瓜田黑料案例库,反转剧情档案,双方回应汇总,舆论风向,澄清汇总",
    path: "/cases",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "案例库｜反转剧情与双方回应完整档案",
        url: "https://stranding.cn/cases",
        inLanguage: "zh-CN",
        description: "按分类收录舆论反转案例，包含时间线、双方回应、风向变化与澄清汇总。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://stranding.cn/" },
          { "@type": "ListItem", position: 2, name: "案例库", item: "https://stranding.cn/cases" },
        ],
      },
    ],
  });

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = cases.filter((item) => {
      const matchKeyword =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.focus.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      const matchCategory = category === "全部" || item.category === category;
      const matchStatus = status === "全部" || item.status === status;
      return matchKeyword && matchCategory && matchStatus;
    });
    const sorted = [...list];
    if (sort === "heat") {
      sorted.sort((a, b) => b.heat - a.heat);
    } else if (sort === "updated") {
      sorted.sort((a, b) => b.updatedDate.localeCompare(a.updatedDate));
    } else {
      sorted.sort((a, b) => a.title.localeCompare(b.title, "zh-Hans-CN"));
    }
    return sorted;
  }, [keyword, category, status, sort]);

  const handleCategory = (next: string) => {
    setCategory(next);
    const nextParams = new URLSearchParams(params);
    if (next === "全部") {
      nextParams.delete("category");
    } else {
      nextParams.set("category", next);
    }
    setParams(nextParams, { replace: true });
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-6 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "案例库" }]} />
            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-8">
                <p className="text-[11px] font-medium tracking-[0.24em] text-secondary-500">ARCHIVE INDEX</p>
                <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
                  案例库：每一场风波的正序档案
                </h1>
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600">
                  这里收录的是已经出现明确舆论方向变化的事件。每条档案都包含四个固定部分：时间线、
                  双方回应、舆论风向变化、逐条澄清。我们不判断谁对谁错，只把顺序和材料摆清楚。
                </p>
              </div>
              <div className="lg:col-span-4">
                <div className="grid grid-cols-3 gap-3 border-l border-background-300 pl-6">
                  <div>
                    <p className="font-heading text-xl font-bold text-foreground-950">{cases.length}</p>
                    <p className="mt-1 text-[11px] text-foreground-500">已归档案例</p>
                  </div>
                  <div>
                    <p className="font-heading text-xl font-bold text-accent-600">
                      {cases.filter((item) => item.status === "反转中").length}
                    </p>
                    <p className="mt-1 text-[11px] text-foreground-500">反转中</p>
                  </div>
                  <div>
                    <p className="font-heading text-xl font-bold text-primary-700">
                      {cases.filter((item) => item.status === "已澄清").length}
                    </p>
                    <p className="mt-1 text-[11px] text-foreground-500">已澄清</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-8 md:py-10">
          <div className="mx-auto max-w-[1440px] px-4 md:px-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="relative w-full lg:max-w-md">
                  <i
                    className="ri-search-2-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                    aria-hidden="true"
                  />
                  <label htmlFor="case-search" className="sr-only">
                    检索案例
                  </label>
                  <input
                    id="case-search"
                    type="search"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder="搜索标题、争议焦点或标签"
                    className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-3 lg:ml-auto">
                  <div className="flex items-center gap-2">
                    <label htmlFor="case-status" className="whitespace-nowrap text-xs text-foreground-600">
                      状态
                    </label>
                    <select
                      id="case-status"
                      value={status}
                      onChange={(event) => setStatus(event.target.value)}
                      className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-900 outline-none focus:border-primary-400"
                    >
                      <option value="全部">全部状态</option>
                      {caseStatuses.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <label htmlFor="case-sort" className="whitespace-nowrap text-xs text-foreground-600">
                      排序
                    </label>
                    <select
                      id="case-sort"
                      value={sort}
                      onChange={(event) => setSort(event.target.value)}
                      className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-900 outline-none focus:border-primary-400"
                    >
                      {sortOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-1 rounded-md border border-background-300 p-1">
                    <button
                      type="button"
                      onClick={() => setView("grid")}
                      aria-label="网格视图"
                      aria-pressed={view === "grid"}
                      className={`flex h-7 w-7 items-center justify-center rounded ${
                        view === "grid" ? "bg-primary-800 text-background-50" : "text-foreground-600 hover:bg-background-100"
                      }`}
                    >
                      <i className="ri-grid-fill text-sm" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setView("list")}
                      aria-label="列表视图"
                      aria-pressed={view === "list"}
                      className={`flex h-7 w-7 items-center justify-center rounded ${
                        view === "list" ? "bg-primary-800 text-background-50" : "text-foreground-600 hover:bg-background-100"
                      }`}
                    >
                      <i className="ri-list-check text-sm" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
                {["全部", ...caseCategories].map((item) => {
                  const isActive = category === item;
                  return (
                    <button
                      key={item}
                      type="button"
                      onClick={() => handleCategory(item)}
                      aria-pressed={isActive}
                      className={[
                        "whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors",
                        isActive
                          ? "bg-secondary-500 font-medium text-background-50"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200",
                      ].join(" ")}
                    >
                      {item}
                    </button>
                  );
                })}
                <span className="ml-auto whitespace-nowrap text-[11px] text-foreground-500">
                  共 {filtered.length} 条结果
                </span>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="mt-8 rounded-lg border border-dashed border-background-300 px-6 py-16 text-center">
                <i className="ri-inbox-archive-line text-3xl text-background-400" aria-hidden="true" />
                <p className="mt-4 font-heading text-base font-semibold text-foreground-900">没有匹配的案例</p>
                <p className="mt-2 text-sm text-foreground-600">试着换个关键词，或把筛选条件放宽一些。</p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    setStatus("全部");
                    handleCategory("全部");
                  }}
                  className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-800 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                >
                  <i className="ri-refresh-line text-base" aria-hidden="true" />
                  重置筛选条件
                </button>
              </div>
            ) : view === "grid" ? (
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((item) => (
                  <CaseCard key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <ul className="mt-6 flex flex-col gap-4">
                {filtered.map((item) => (
                  <li
                    key={item.id}
                    className="group grid grid-cols-1 gap-5 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:grid-cols-12 md:p-5"
                  >
                    <Link to={`/cases/${item.slug}`} className="block md:col-span-4 lg:col-span-3">
                      <div className="h-44 w-full overflow-hidden rounded-md bg-background-200 md:h-32">
                        <img
                          src={item.cover}
                          alt={`${item.title}案例配图`}
                          title={`${item.title} - 瓜田黑料案例档案`}
                          className="h-44 w-full object-top md:h-32"
                        />
                      </div>
                    </Link>
                    <div className="flex flex-col md:col-span-8 lg:col-span-9">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-900">
                          {item.category}
                        </span>
                        <StatusBadge label={item.status} />
                        <span className="ml-auto flex items-center gap-1 whitespace-nowrap text-[11px] text-foreground-500">
                          <i className="ri-fire-line text-sm text-accent-500" aria-hidden="true" />
                          热度 {item.heat}
                        </span>
                      </div>
                      <h2 className="mt-3 font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
                        <Link to={`/cases/${item.slug}`} className="transition-colors hover:text-primary-700">
                          {item.title}
                        </Link>
                      </h2>
                      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-focus-3-line text-sm" aria-hidden="true" />
                          {item.focus}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
      <ScrollTools />
    </div>
  );
}